import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import classroomService from '../../services/classroomService';
import organizacionAcademicaService from '../../services/organizacionAcademicaService';
import docentesService from '../../services/docentesService';
import estudiantesService from '../../services/estudiantesService';
import utils from '../../utils/generalFunctions';
import types from '../../services/types';
import Loading from '../../components/Loading';
import './AulaVirtual.css';

// Lista de aulas del usuario autenticado. El docente ve las aulas de las
// materias que dicta (para gestionarlas); el estudiante ve las aulas de las
// materias de su curso (para verlas y participar). Cada aula la resuelve el
// backend automáticamente a partir de su academicSetting.
const ClassroomHome = () => {
  const [loading, setLoading] = useState(true);
  const [aulas, setAulas] = useState([]);
  const [esDocente, setEsDocente] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [sinInformacion, setSinInformacion] = useState(false);

  useEffect(() => {
    const cargarAulas = async () => {
      try {
        const user = utils.getUserInformation();
        const esProfesor = Number(user?.userTypeId) === types.userTypes.TEACHER;
        setEsDocente(esProfesor);

        let settings = [];
        if (esProfesor) {
          const docente = await docentesService.getDocenteMe();
          if (!docente || !docente.teacherId) {
            setSinInformacion(true);
            setLoading(false);
            return;
          }
          const todos = await organizacionAcademicaService.getAcademicOrganizations();
          settings = (todos || []).filter((s) => Number(s.teacherId) === Number(docente.teacherId));
        } else {
          const estudiante = await estudiantesService.getEstudiantesPorUsuario();
          if (!estudiante || !estudiante.courseId) {
            setSinInformacion(true);
            setLoading(false);
            return;
          }
          settings = await organizacionAcademicaService.getAcademicOrganizationsByCourse(
            estudiante.courseId,
            estudiante.schoolYearId
          );
        }

        const aulasResueltas = await Promise.all(
          (settings || []).map(async (setting) => {
            try {
              const aula = await classroomService.getClassroomByAcademicSetting(setting.academicSettingId);
              if (!aula || !aula.classroomId) return null;
              return { ...aula, schoolSubjectName: setting.schoolSubjectName, courseName: setting.courseName, teacherName: setting.teacherName };
            } catch {
              return null;
            }
          })
        );
        setAulas(aulasResueltas.filter(Boolean));
      } catch (error) {
        console.error('Error al cargar las aulas:', error);
      }
      setLoading(false);
    };

    cargarAulas();
  }, []);

  if (loading) return <Loading />;

  const aulasFiltradas = aulas.filter((a) =>
    `${a.schoolSubjectName} ${a.courseName}`.toLowerCase().includes(busqueda.toLowerCase())
  );

  return (
    <div className="container-fluid p-0 av-module">
      <h1 className="h3 mb-1">Mis Aulas Virtuales</h1>
      <p className="text-muted mb-3">
        {esDocente ? 'Elige un aula para gestionar su contenido.' : 'Elige un aula para ver sus clases y tareas.'}
      </p>

      {sinInformacion && (
        <div className="alert alert-warning">
          No encontramos tu información de {esDocente ? 'docente' : 'estudiante'} asociada a esta cuenta.
        </div>
      )}

      {!sinInformacion && (
        <>
          <div className="mb-4" style={{ maxWidth: '360px' }}>
            <input
              type="text"
              className="form-control"
              placeholder="Buscar un aula por materia o curso..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              aria-label="Buscar aula"
            />
          </div>

          {aulasFiltradas.length === 0 ? (
            <div className="alert alert-info">
              {esDocente
                ? 'Todavía no tienes materias asignadas con aula virtual.'
                : 'Todavía no hay aulas virtuales disponibles para tu curso.'}
            </div>
          ) : (
            <div className="row">
              {aulasFiltradas.map((aula) => (
                <div className="col-12 col-md-6 col-lg-4 mb-4" key={aula.classroomId}>
                  <div className="card h-100 shadow-sm av-course-card">
                    <div className="av-course-banner" style={{ backgroundColor: '#4f8cff' }}>
                      <i className="fas fa-chalkboard" aria-hidden="true"></i>
                    </div>
                    <div className="card-body d-flex flex-column">
                      <h5 className="card-title mb-1">{aula.schoolSubjectName}</h5>
                      <p className="text-muted small mb-2">
                        {aula.courseName} {!esDocente && aula.teacherName ? `· Prof. ${aula.teacherName}` : ''}
                      </p>
                      <div className="mt-auto">
                        <Link
                          to={esDocente ? `/aula/gestionar/${aula.classroomId}` : `/aula/ver/${aula.classroomId}`}
                          className="btn btn-primary w-100"
                        >
                          {esDocente ? 'Gestionar aula' : 'Entrar al aula'}{' '}
                          <i className="fas fa-arrow-right ms-1" aria-hidden="true"></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ClassroomHome;
